<script setup lang="ts">
import type { NavItem } from '~~/shared/types/store'
import { ALL_CATEGORIES_HREF } from '~~/shared/utils/allCategoriesLink'

const props = defineProps<{ item: NavItem }>()

const MAX_COLS = 4
const MAX_LINKS_PER_COL = 8
const MAX_FLAT_LINKS = MAX_COLS * MAX_LINKS_PER_COL
const MAX_ITEMS_PER_GROUP = 8

// Sekcje 'fashion' mają kuratorowane grupy; reszta (Dom i Ogród, Uroda…) to
// płaska lista kategorii składana z children.
const groups = computed(() => props.item.groups ?? [])
const recommended = computed(() => props.item.recommended ?? [])
const isFlat = computed(() => groups.value.length === 0)

// Płaski układ: liście dzielone równo na kolumny.
const flatColumns = computed<NavItem[][]>(() => {
  if (!isFlat.value) return []
  const links = props.item.children.slice(0, MAX_FLAT_LINKS)
  const perCol = Math.ceil(links.length / MAX_COLS)
  const cols: NavItem[][] = []
  for (let i = 0; i < MAX_COLS; i++) {
    const chunk = links.slice(i * perCol, (i + 1) * perCol)
    if (chunk.length) cols.push(chunk)
  }
  return cols
})

function initial(label: string): string {
  return label.trim().charAt(0).toUpperCase()
}
</script>

<template>
  <div class="mega">
    <div class="mega__inner container">

      <!-- Sekcja modowa: polecane kategorie + grupy -->
      <template v-if="!isFlat">
        <div v-if="recommended.length" class="mega__reco">
          <p class="mega__reco-cap">Polecane</p>
          <div class="mega__reco-row">
            <NuxtLink
              v-for="r in recommended"
              :key="r.id"
              :to="r.href"
              class="mega__reco-tile"
            >
              <span class="mega__reco-thumb">
                <img
                  v-if="r.thumbnail"
                  :src="r.thumbnail"
                  :alt="r.label"
                  class="mega__reco-img"
                  loading="lazy"
                >
                <span v-else class="mega__reco-ph" aria-hidden="true">{{ initial(r.label) }}</span>
              </span>
              <span class="mega__reco-label">{{ r.label }}</span>
            </NuxtLink>
          </div>
        </div>

        <div class="mega__groups">
          <div v-for="g in groups" :key="g.title" class="mega__col">
            <p class="mega__col-heading">{{ g.title }}</p>
            <ul class="mega__col-list">
              <li v-for="sub in g.items.slice(0, MAX_ITEMS_PER_GROUP)" :key="sub.href">
                <NuxtLink :to="sub.href" class="mega__link">{{ sub.label }}</NuxtLink>
              </li>
              <li v-if="g.items.length > MAX_ITEMS_PER_GROUP">
                <NuxtLink :to="item.href" class="mega__link mega__link--more">
                  Zobacz wszystkie →
                </NuxtLink>
              </li>
            </ul>
          </div>
        </div>
      </template>

      <!-- Dział niepogrupowany: płaska lista w kolumnach -->
      <div v-else class="mega__cols">
        <div v-for="(col, i) in flatColumns" :key="i" class="mega__col">
          <ul class="mega__col-list">
            <li v-for="link in col" :key="link.href">
              <NuxtLink :to="link.href" class="mega__link">{{ link.label }}</NuxtLink>
            </li>
          </ul>
        </div>
      </div>

    </div>

    <div class="mega__all">
      <NuxtLink :to="ALL_CATEGORIES_HREF" class="mega__all-link">
        {{ $t('nav.viewAllCategories') }}
        <span aria-hidden="true">→</span>
      </NuxtLink>
    </div>
  </div>
</template>

<style scoped>
.mega {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-bg, #f9f9f9);
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  z-index: 99;
}

.mega__inner {
  display: flex;
  flex-direction: column;
  gap: 36px;
  padding-top: 40px;
  padding-bottom: 44px;
}

/* Polecane — wiersz kafli kategorii */
.mega__reco {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mega__reco-cap {
  margin: 0;
  font-size: 0.75rem;
  font-weight: var(--font-weight-normal);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-muted);
}

.mega__reco-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.mega__reco-tile {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 150px;
  text-decoration: none;
  color: inherit;
}

.mega__reco-thumb {
  width: 150px;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  background: var(--color-border);
  border-radius: var(--product-img-radius);
  display: flex;
  align-items: center;
  justify-content: center;
}

.mega__reco-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.mega__reco-tile:hover .mega__reco-img {
  transform: scale(1.03);
}

.mega__reco-ph {
  font-size: 2rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-muted);
  opacity: 0.5;
}

.mega__reco-label {
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 1.3;
}

/* Grupy — siatka zawijana (obsługuje 4–8 grup) */
.mega__groups {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 28px 40px;
}

/* Płaski układ (dział niepogrupowany) */
.mega__cols {
  display: flex;
  gap: 48px;
}

.mega__col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 120px;
}

.mega__col-heading {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1;
}

.mega__col-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mega__link {
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 1.2;
  transition: opacity 0.15s;
}

.mega__link:hover {
  opacity: 0.6;
}

.mega__link--more {
  font-weight: var(--font-weight-normal);
  white-space: nowrap;
}

/* Spotkanie 30.10.2026 (proles): „powiększyć napisy w menu" — działy 15→17 px,
   podkategorie 13→15 px; przy skalowaniu 125% w Windows 13 px było za drobne. */
[data-brand="proles"] .mega__col-heading {
  font-size: 1.0625rem;
}

[data-brand="proles"] .mega__link {
  font-size: 0.9375rem;
}

/* Stopka: link do listingu wszystkich kategorii */
.mega__all {
  border-top: 1px solid var(--color-border);
}

.mega__all-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 0;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-normal);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-black);
  text-decoration: none;
}

.mega__all-link:hover {
  text-decoration: underline;
}
</style>
